import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { login as loginApi, confirmRegistration, forgotPassword } from 'src/api/auth';
import { getProfileApi } from 'src/api';
import { preloadOrganizations } from 'src/hooks/organization/useOrganizations';
import { preloadProfilePicture } from 'src/hooks/profile/useProfilePicture';
import { loginSchema, LoginFormData } from 'src/utils/validationSchemas';
import { PATH_AUTH, PATH_ROOT } from 'src/routes/paths';
import {useAuth} from 'src/hooks';
import { UserProfile } from 'src/types/common.types';
import { UserAccountState } from 'src/enums/shared.enums';
import { isMinimalProfile } from 'src/utils/profileUtils';

export const useLoginForm = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, profile } = useAuth();
  
  // Form setup
  const methods = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      username: "",
      password: ""
    }
  });
  const { handleSubmit, setValue, watch } = methods;
  
  // Watch form values
  const watchedUsername = watch("username");
  const watchedPassword = watch("password");
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Helper functions
  const validateEmailForVerification = () => {
    const emailToVerify = watchedUsername || '';
    if (!emailToVerify) {
      setErrorMessage("Email address is missing. Please enter your email first.");
      return null;
    }
    return emailToVerify;
  };

  const createUserProfile = (username: string): UserProfile => ({
    email: username,
    displayName: username.split('@')[0],
    firstName: '',
    lastName: '',
    bio: '',
    state: UserAccountState.PENDING,
    accountType: "artist",
    createdAt: new Date().toISOString(),
  });

  // Event handlers
  const handleCheckboxChange = () => {
    setRememberMe(prev => !prev);
  };

  const handleVerificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVerificationCode(e.target.value);
  };

  const handleResendVerification = async () => {
    const emailToVerify = validateEmailForVerification();
    if (!emailToVerify) return;

    try {
      setIsLoading(true);
      const success = await forgotPassword(emailToVerify);
      
      if (success) {
        toast.success("Verification code sent to your email");
      } else {
        throw new Error("Failed to send verification code");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to resend verification code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!verificationCode) {
      setErrorMessage("Verification code is required");
      return;
    }

    const emailToVerify = validateEmailForVerification();
    if (!emailToVerify) return;

    try {
      setIsLoading(true);
      const success = await confirmRegistration(emailToVerify, verificationCode);
      
      if (success) {
        toast.success("Account verified successfully");
        setShowVerification(false);
        setErrorMessage("");
        // Try to log in automatically after successful verification
        await handleLogin({ username: watchedUsername, password: watchedPassword });
      } else {
        throw new Error("Verification failed");
      }
    } catch (error: any) {
      setErrorMessage(error.message || "Invalid verification code");
      toast.error(error.message || "Failed to verify account");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessfulLogin = async (response: any, formData: LoginFormData) => {
    try {
      // Import persistStore
      const { persistStore } = await import('src/persist-store');
      
      // Save username to session storage to use as userId
      await persistStore.putSession('userEmail', formData.username);

      // Handle remember me preference
      if (rememberMe) {
        await persistStore.putLocal('rememberMe', 'true');
        await persistStore.putLocal('username', formData.username);
      } else {
        await persistStore.removeLocal('rememberMe');
      }

      // Get tokens from response
      const tokens = response.tokens;

      // Create a user profile object
      let userProfile = createUserProfile(formData.username);

      // Store user ID in session storage
      await persistStore.putSession('userId', formData.username);

      // Store minimal profile data in session storage
      await persistStore.putSession('userProfile', userProfile);

      // Fetch actual profile to determine onboarding state
      try {
        const profileResponse = await getProfileApi();
        if (profileResponse?.profile) {
          const apiProfile = profileResponse.profile;
          const profileData: UserProfile = {
            ...apiProfile,
            state: isMinimalProfile(apiProfile) ? UserAccountState.PENDING : UserAccountState.ONBOARDED
          };
          await persistStore.putSession('userProfile', profileData);
          userProfile = profileData;
        }
      } catch (profileError) {
        console.error("Error fetching profile data:", profileError);
      }

      // Set auth state immediately — profile picture loads in the background
      login(tokens.IdToken, userProfile);

      // Pre-fetch data needed after login (non-blocking)
      preloadOrganizations();
      preloadProfilePicture();
    } catch (error) {
      console.error("Error during login:", error);
      toast.error("Error processing login data.");
      setIsLoading(false);
    }
  };

  const handleLoginError = (error: any) => {
    setIsLoading(false);

    console.error("Login error:", error);
    
    // Extract error message from Cognito error
    let errorMessage = "Login failed. Please try again.";
    let errorName = "";
    
    if (error.name) {
      errorName = error.name;
    } else if (error.message && error.message.includes("UserNotConfirmedException")) {
      errorName = "UserNotConfirmedException";
    } else if (error.message && error.message.includes("NotAuthorizedException")) {
      errorName = "NotAuthorizedException";
    } else if (error.message && error.message.includes("UserNotFoundException")) {
      errorName = "UserNotFoundException";
    }
    
    // Handle specific Cognito error cases
    switch (errorName) {
      case 'UserNotConfirmedException':
        toast.info("Your account needs verification. Please enter the code sent to your email.");
        setShowVerification(true);
        break;
      case 'NotAuthorizedException':
        toast.error("Incorrect email or password. Please try again.");
        break;
      case 'UserNotFoundException':
        toast.error("We couldn't find an account with that email. Please check your email or sign up.");
        break;
      case 'PasswordResetRequiredException':
        toast.info("You need to reset your password. Redirecting to password reset page...");
        setTimeout(() => navigate('/forgot-password'), 2000);
        break;
      default:
        toast.error(error.message || errorMessage);
    }
  };

  const handleLogin = async (formData: LoginFormData) => {
    try {
      const tokens = await loginApi(formData.username, formData.password, rememberMe);

      if (tokens) {
       
        
        const response = {
          tokens: {
            AccessToken: tokens.accessToken,
            IdToken: tokens.idToken,
            RefreshToken: tokens.refreshToken
          }
        };
        await handleSuccessfulLogin(response, formData);
        toast.success("Login successful");
      } else {
        throw new Error("Login failed. No tokens received.");
      }
    } catch (error: any) {
      handleLoginError(error);
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    await handleLogin(data);
  };

  const loadSavedCredentials = useCallback(async () => {
    try {
      const { persistStore } = await import('src/persist-store');
      const savedUsername = await persistStore.getLocal('username');
      const savedRememberMe = await persistStore.getLocal('rememberMe') === 'true';

      if (savedUsername && savedRememberMe) {
        setValue('username', savedUsername as string);
        setRememberMe(true);
      }
    } catch (error) {
      console.error('Error loading saved credentials:', error);
    }
  }, [setValue]);

  // Check if user should be redirected
  const shouldRedirect = useCallback(() => {
    if (isAuthenticated && profile) {
      // Check if user is onboarded by checking their state
      if (profile.state === UserAccountState.ONBOARDED) {
        navigate(PATH_ROOT.organization.organizations);
        return true;
      }
      navigate(PATH_AUTH.onboarding);
      return true;
    }
    return false;
  }, [isAuthenticated, profile, navigate]);

  return {
    // Form methods
    methods,
    onSubmit: handleSubmit(onSubmit),
    
    // Form data
    watchedUsername,
    watchedPassword,
    
    // State
    isLoading,
    rememberMe,
    showVerification,
    setShowVerification,
    verificationCode,
    errorMessage,
    setErrorMessage,
    
    // Handlers
    handleCheckboxChange,
    handleVerificationChange,
    handleVerification,
    handleResendVerification,
    loadSavedCredentials,
    shouldRedirect,
    
    // Auth state
    isAuthenticated,
    profile
  };
};